"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ArrowRight, Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
export default function CookieManagement() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const protocolItems = [{
    index: "§ C.01",
    title: "Cookie Tecnici",
    body: "Questi cookie sono essenziali per il corretto funzionamento del sito. Garantiscono la navigazione sicura e la gestione delle preferenze di base, senza raccogliere dati personali identificativi.",
    bullets: ["Sessione di navigazione", "Preferenze lingua", "Sicurezza del sito", "Bilanciamento del carico"]
  }, {
    index: "§ C.02",
    title: "Cookie Analitici",
    body: "Utilizziamo strumenti di analisi per comprendere come i pazienti interagiscono con la nostra guida. Questi dati ci aiutano a migliorare l'esperienza utente e la chiarezza delle informazioni fornite.",
    bullets: ["Statistiche di traffico", "Pagine più visitate", "Tempo di permanenza", "Origine del traffico"]
  }, {
    index: "§ C.03",
    title: "Cookie di Profilazione",
    body: "Non utilizziamo cookie di profilazione di terze parti per scopi pubblicitari. La nostra missione è fornire una guida indipendente e trasparente, libera da influenze commerciali esterne.",
    bullets: ["Nessuna tracciabilità esterna", "Nessun annuncio mirato", "Rispetto della privacy", "Dati anonimizzati"]
  }, {
    index: "§ C.04",
    title: "Gestione Consenso",
    body: "Hai il pieno controllo sui tuoi dati. Puoi modificare le tue preferenze in qualsiasi momento tramite il pannello di gestione o le impostazioni del tuo browser, garantendo la massima trasparenza.",
    bullets: ["Revoca del consenso", "Modifica preferenze", "Cancellazione dati", "Aggiornamento policy"]
  }, {
    index: "§ C.05",
    title: "Integrità dei Dati",
    body: "La protezione dei tuoi dati è la nostra priorità. Tutte le informazioni raccolte sono trattate in conformità con il GDPR, garantendo che la tua ricerca di cure dentali rimanga privata.",
    bullets: ["Conformità GDPR", "Crittografia avanzata", "Accesso limitato", "Conservazione minima"]
  }, {
    index: "§ C.06",
    title: "Contatti Privacy",
    body: "Per qualsiasi domanda riguardante il trattamento dei tuoi dati personali o per esercitare i tuoi diritti, il nostro team è a disposizione per fornirti assistenza immediata e trasparente.",
    bullets: ["Supporto dedicato", "Richieste DPO", "Trasparenza totale", "Risposta entro 48h"]
  }];
  return <section data-section-id="1288" id="cookiemanagement" className="relative bg-background text-foreground py-28 md:py-40 overflow-hidden">
      <div ref={ref} className="relative mx-auto max-w-[1440px] px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 relative">
            <div className="lg:sticky lg:top-32">
              <div aria-hidden="true" className="pointer-events-none absolute -top-12 -left-6 select-none font-sans leading-none text-foreground/[0.04]" style={{
              fontSize: "clamp(12rem, 22vw, 22rem)"
            }}>
                04
              </div>

              <div className={`relative flex items-center gap-4 transition-all duration-[520ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                <span className="font-mono text-xs md:text-sm font-medium uppercase tracking-[0.18em] text-foreground">
                  § 04 / Privacy
                </span>
                <span className="h-px w-10 bg-foreground/40" />
              </div>

              <h2 className={`relative mt-8 font-default uppercase leading-[0.86] tracking-tight text-foreground transition-all duration-[520ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
              fontSize: "clamp(3rem, 8vw, 7.5rem)",
              transitionDelay: "80ms"
            }}>
                Gestione{" "}
                <span className="font-serif italic text-primary" style={{
                letterSpacing: "0.01em"
              }}>
                  Cookie
                </span>
              </h2>

              <div className={`relative mt-10 flex items-center transition-all duration-700 ease-out ${isVisible ? "opacity-100" : "opacity-0"}`} style={{
              transitionDelay: "200ms"
            }} aria-hidden="true">
                <div className="h-px w-32 bg-primary origin-left" style={{
                transform: isVisible ? "rotate(-6deg) scaleX(1)" : "rotate(-6deg) scaleX(0)",
                transition: "transform 700ms ease-out",
                transitionDelay: "300ms"
              }} />
                <span className="ml-1 inline-block h-1.5 w-1.5 bg-primary" />
              </div>

              <p className={`relative mt-10 max-w-md font-default text-base md:text-lg leading-relaxed text-muted-foreground transition-all duration-[520ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
              fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
              letterSpacing: "-0.005em",
              transitionDelay: "240ms"
            }}>
                La trasparenza è il pilastro della nostra guida. Gestiamo i tuoi dati con la massima serietà, garantendo un'esperienza di navigazione sicura, privata e focalizzata esclusivamente sulla tua salute dentale.
              </p>

              <div className={`relative mt-10 transition-all duration-[520ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
              transitionDelay: "320ms"
            }}>
                <Link to="/PrivacyPolicyPage" className="group inline-flex items-center gap-3 font-mono text-xs md:text-sm font-medium uppercase tracking-[0.14em] text-primary">
                  <span className="relative">
                    Leggi la Policy Completa
                    <span className="pointer-events-none absolute -bottom-1 left-0 h-px w-0 bg-primary transition-all duration-[220ms] ease-out group-hover:w-full" />
                  </span>
                  <ArrowRight className="size-4 transition-transform duration-[220ms] ease-out group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full">
              {protocolItems.map((item, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className={`group border-b border-border last:border-b-0 transition-all duration-[520ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
              transitionDelay: `${120 + i * 80}ms`
            }}>
                  <AccordionTrigger className="flex w-full items-center justify-between gap-6 py-7 hover:no-underline [&>svg]:hidden">
                    <div className="flex flex-1 items-baseline gap-6 md:gap-10 text-left">
                      <span className="font-mono text-xs md:text-sm font-medium uppercase tracking-[0.18em] text-foreground shrink-0 w-16 md:w-20">
                        {item.index}
                      </span>
                      <h3 className="font-default uppercase leading-[0.92] tracking-tight text-foreground" style={{
                    fontSize: "clamp(1.5rem, 3vw, 2.75rem)"
                  }}>
                        {item.title}
                      </h3>
                    </div>
                    <span className="shrink-0 inline-flex size-8 items-center justify-center text-primary transition-transform duration-[240ms] ease-out group-data-[state=open]:rotate-45">
                      <Plus className="size-7" strokeWidth={1.5} />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="border-t border-primary bg-card/60 px-0 pb-0">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 px-6 md:px-8 py-8">
                      <div className="md:col-span-7">
                        <p className="font-default text-base leading-relaxed text-foreground" style={{
                      fontSize: "clamp(0.9375rem, 1vw, 1rem)",
                      lineHeight: 1.65
                    }}>
                          {item.body}
                        </p>
                      </div>
                      <ul className="md:col-span-5 space-y-3">
                        {item.bullets.map((bullet, b) => <li key={b} data-index={b} className="flex items-start gap-3 font-mono text-xs uppercase tracking-[0.14em] text-muted-foreground">
                            <span className="mt-1.5 inline-block size-1.5 bg-primary shrink-0" />
                            <span>{bullet}</span>
                          </li>)}
                      </ul>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}